<template>
  <!-- As fórmulas do cálculo — e, no "quando", a da condição — ficam embaixo das duas colunas, na largura
       toda (o `#bottom` do formulário) -->
  <ontos-choice-toggle v-if="offersComputed" :model-value="computedMode" :label="labeled ? filling.label : ''"
                       :options="computedOptions" :disabled="!!computeBlocked" :disabled-reason="computeBlocked"
                       @update:model-value="setComputed($event)"/>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import ChoiceToggle, {ChoiceOption} from '@/components/shared/ChoiceToggle.vue'
import {isMedia} from '@/utils/mediaTypes'
import {MARKS} from '@/utils/propertyDefinition'
import {pairOf} from '@/components/design/property/pairs'
import type {BehaviorModel} from '@/components/design/property/PropertyBehavior.vue'

/** As três do Preenchimento: quem edita preenche, a fórmula calcula sempre, ou só quando a condição dá Sim. */
type ComputedMode = 'filled' | 'always' | 'when'

/**
 * Se há Preenchimento a escolher. Mídia é arquivo enviado, e não valor que se calcula. Exportado para a seção
 * em volta, no atributo, sumir junto com ele — sem isso ficava o título "Preenchimento" sobre nada.
 */
export function offersComputed(kind: 'attribute' | 'relationship', type: string | undefined): boolean {
  return kind === 'relationship' || !isMedia(type)
}

/**
 * O Preenchimento de uma propriedade: Preenchido, Calculado e — só no atributo — Calculado quando. Mexe direto
 * no `model` do editor, como o Comportamento.
 *
 * À parte do Comportamento porque é ele que decide o que o Comportamento mostra (pedido do usuário: embaixo do
 * Tipo, na coluna da esquerda do atributo — o que o campo É; trocá-lo fazia as escolhas de cima aparecerem e
 * sumirem). No relacionamento continua dentro do Comportamento, na caixa do dono.
 */
@Component({name: 'ontos-property-filling', components: {'ontos-choice-toggle': ChoiceToggle}})
class PropertyFilling extends Vue {
  @Prop({type: Object, required: true}) readonly model!: BehaviorModel
  @Prop({type: String, required: true}) readonly kind!: 'attribute' | 'relationship'
  /** Por que o "Calculado" não se liga — o relacionamento com espelho —, ou vazio. */
  @Prop({type: String, default: ''}) readonly computeBlocked!: string
  /** Com o rótulo "Preenchimento" em cima; sem ele, quando a seção em volta já o diz. */
  @Prop({type: Boolean, default: true}) readonly labeled!: boolean

  get offersComputed(): boolean {
    return offersComputed(this.kind, this.model.type)
  }

  get filling() {
    return pairOf(this.$tt, 'computed', 'pair.filling', ['pair.filled', 'pair.filled-hint'], ['computed', 'pair.computed-hint'])
  }

  /**
   * O Preenchimento escolhido. Com a condição (ou começando a escrevê-la), "quando" — as duas fórmulas
   * moram no formulário, embaixo das colunas.
   */
  get computedMode(): ComputedMode {
    if (!this.model.computed) return 'filled'
    return this.model.computedWhen !== undefined ? 'when' : 'always'
  }

  /** As três respostas — o "quando" só no atributo (ver `behaviorValue`) —, as calculadas com a marca. */
  get computedOptions(): ChoiceOption<ComputedMode>[] {
    const {off, on} = this.filling
    const options: ChoiceOption<ComputedMode>[] = [{value: 'filled', ...off}, {value: 'always', ...on}]
    if (this.kind === 'attribute') options.push({value: 'when', label: on.label, qualifier: this.$tt('pair.when') as string,
      hint: this.$tt('pair.computed-when-hint') as string, ...MARKS.computed, conditional: true})
    return options
  }

  /**
   * A condição escrita antes de trocar de opção: voltar ao "quando" a traz de volta, como a do "Obrigatório
   * quando" (`lastRequiredWhen`, no Comportamento).
   */
  lastComputedWhen = ''

  setComputed(mode: ComputedMode) {
    this.model.computed = mode !== 'filled'
    if (mode === 'when') {
      this.model.computedWhen = this.model.computedWhen ?? this.lastComputedWhen
      return
    }
    if (this.model.computedWhen !== undefined) this.lastComputedWhen = this.model.computedWhen
    this.model.computedWhen = undefined
  }
}

export default toNative(PropertyFilling)
</script>
